import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, ApiClientError, type ServiceWritePayload } from '../../api';
import {
  EmptyState,
  ErrorBox,
  Loader,
  PageHeader,
  formatDuration,
  formatPrice,
} from '../../components/Common';
import type { Service } from '../../types';

export function AdminServicesPage() {
  const queryClient = useQueryClient();
  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['admin-services'],
    queryFn: () => api.services.listAdmin(),
  });

  const [editing, setEditing] = useState<Service | 'new' | null>(null);

  const toggle = useMutation({
    mutationFn: (s: Service) =>
      s.is_active ? api.services.deactivate(s.id) : api.services.activate(s.id),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['admin-services'] });
      void queryClient.invalidateQueries({ queryKey: ['services'] });
    },
  });

  return (
    <div>
      <PageHeader
        title="Каталог услуг"
        action={
          <button className="btn-primary" onClick={() => setEditing('new')}>
            +
          </button>
        }
      />

      {isLoading && <Loader />}
      {error && <ErrorBox message={(error as Error).message} onRetry={() => void refetch()} />}
      {!isLoading && data && data.length === 0 && (
        <EmptyState icon="📭" title="Услуг ещё нет" hint="Нажмите + чтобы добавить" />
      )}

      <ul className="space-y-2">
        {data?.map((s) => (
          <li
            key={s.id}
            className={`card flex items-center justify-between ${
              !s.is_active ? 'opacity-60' : ''
            }`}
          >
            <button className="flex-1 text-left" onClick={() => setEditing(s)}>
              <p className="font-semibold">{s.name}</p>
              <p className="text-xs text-tg-hint">
                {formatPrice(s.price_min, s.price_max)} ·{' '}
                {formatDuration(s.duration_minutes)}
              </p>
              {!s.is_active && <p className="text-xs text-red-400">деактивирована</p>}
            </button>
            <button
              className="btn-secondary"
              onClick={() => toggle.mutate(s)}
              disabled={toggle.isPending}
            >
              {s.is_active ? 'Скрыть' : 'Включить'}
            </button>
          </li>
        ))}
      </ul>

      {editing && (
        <ServiceEditor
          service={editing === 'new' ? null : editing}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            void queryClient.invalidateQueries({ queryKey: ['admin-services'] });
            void queryClient.invalidateQueries({ queryKey: ['services'] });
          }}
        />
      )}
    </div>
  );
}

function ServiceEditor({
  service,
  onClose,
  onSaved,
}: {
  service: Service | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [name, setName] = useState(service?.name ?? '');
  const [desc, setDesc] = useState(service?.description ?? '');
  const [priceMin, setPriceMin] = useState(service?.price_min ?? '0');
  const [priceMax, setPriceMax] = useState(service?.price_max ?? '');
  const [duration, setDuration] = useState(service?.duration_minutes ?? 60);

  const save = useMutation({
    mutationFn: () => {
      const payload: ServiceWritePayload = {
        name,
        description: desc || null,
        price_min: Number(priceMin),
        price_max: priceMax ? Number(priceMax) : null,
        duration_minutes: duration,
      };
      if (service) {
        return api.services.update(service.id, payload);
      }
      return api.services.create(payload);
    },
    onSuccess: onSaved,
  });

  return (
    <div className="fixed inset-0 z-30 flex items-end justify-center bg-black/40 p-4 sm:items-center">
      <div className="w-full max-w-md rounded-tg bg-tg-bg p-4">
        <div className="mb-3 flex items-center justify-between">
          <h2 className="font-semibold">{service ? 'Редактировать' : 'Новая услуга'}</h2>
          <button className="text-tg-hint" onClick={onClose}>
            ✕
          </button>
        </div>
        <div className="space-y-3">
          <Field label="Название" value={name} onChange={setName} />
          <div>
            <label className="label">Описание</label>
            <textarea
              className="input min-h-[80px]"
              value={desc}
              onChange={(e) => setDesc(e.target.value)}
            />
          </div>
          <div className="grid grid-cols-2 gap-2">
            <Field label="Цена мин" value={priceMin} onChange={setPriceMin} type="number" />
            <Field label="Цена макс" value={priceMax} onChange={setPriceMax} type="number" />
          </div>
          <Field
            label="Длительность, мин"
            value={String(duration)}
            onChange={(v) => setDuration(Number(v))}
            type="number"
          />
        </div>

        {save.error && (
          <ErrorBox
            message={
              save.error instanceof ApiClientError
                ? save.error.message
                : (save.error as Error).message
            }
          />
        )}

        <div className="mt-3 flex gap-2">
          <button className="btn-secondary flex-1" onClick={onClose}>
            Отмена
          </button>
          <button
            className="btn-primary flex-1"
            disabled={!name || save.isPending}
            onClick={() => save.mutate()}
          >
            {save.isPending ? 'Сохраняем...' : 'Сохранить'}
          </button>
        </div>
      </div>
    </div>
  );
}

function Field({
  label,
  value,
  onChange,
  type = 'text',
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  type?: string;
}) {
  return (
    <div>
      <label className="label">{label}</label>
      <input
        type={type}
        className="input"
        value={value}
        onChange={(e) => onChange(e.target.value)}
      />
    </div>
  );
}
